import React from 'react'
import { cn } from './cn'

/**
 * Compact production checkbox. The native input covers the visible square so
 * modifier clicks (for example Shift range selection) preserve browser-native
 * event semantics; do not replace it with an sr-only input plus label relay.
 */
export interface CheckboxProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  'type' | 'size'
> {
  /** Providing a label makes the whole row the desktop-sized click target. */
  label?: React.ReactNode
  /** Uses the native `HTMLInputElement.indeterminate` state. */
  indeterminate?: boolean
  description?: string
}

export const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate = false, className, ...rest },
  forwardedRef
) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  React.useImperativeHandle(forwardedRef, () => inputRef.current as HTMLInputElement)

  React.useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate
  }, [indeterminate])

  return (
    <label
      className={cn(
        'inline-flex min-h-6 cursor-pointer items-start gap-2 text-base text-foreground',
        className
      )}
    >
      <span className="relative size-[15px] shrink-0">
        <input
          ref={inputRef}
          type="checkbox"
          className="peer absolute inset-0 z-10 size-full cursor-pointer opacity-0"
          {...rest}
        />
        <span
          data-checkbox-indicator={indeterminate ? 'indeterminate' : undefined}
          className={cn(
            'pointer-events-none relative grid size-full place-items-center rounded-[2px] border border-muted bg-background peer-checked:border-foreground peer-checked:bg-foreground peer-checked:[&>[data-checkbox-check]]:opacity-100 peer-focus-visible:outline-2 peer-focus-visible:outline-foreground peer-focus-visible:outline-offset-2 peer-disabled:cursor-not-allowed peer-disabled:opacity-40',
            indeterminate && 'border-foreground bg-foreground'
          )}
          aria-hidden="true"
        >
          <svg
            data-checkbox-check
            className="size-2.5 opacity-0"
            viewBox="0 0 16 16"
            fill="none"
            stroke="var(--bg)"
            strokeWidth="1.7"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="m3 8 3 3 7-7" />
          </svg>
          {indeterminate ? (
            <span
              data-checkbox-indeterminate
              className="absolute h-0.5 w-2.5 rounded-full bg-background"
            />
          ) : null}
        </span>
      </span>
      {label != null || description ? (
        <span className="min-w-0">
          {label != null ? <span className="block">{label}</span> : null}
          {description ? (
            <span className="mt-0.5 block text-xs text-muted">{description}</span>
          ) : null}
        </span>
      ) : null}
    </label>
  )
})
